import { useCallback, useState, useEffect, useRef } from 'react';
import { Box, Tabs } from '@lucid/lego';
import { useStore } from '../../store/store';
import { useLabels } from '../../hooks/useLabels';
import { convertToContentfulLocale } from '../../utils/tools';
import { useGetLocation, useGetCheckoutDetail } from '../../api/services/lupp/inventory/inventory';
import type { TransformedCheckoutDetail } from '../../api/services/lupp/inventory/transformers.checkout';
import { createEmptyVehicleRow, type InventoryTableRow } from './utils';
import ConfigureShortCode from './ConfigureShortCode/ConfigureShortCode';
import Checkout from '../Checkout/Checkout';
import type { ProspectDetails } from '../Checkout/Checkout';
import AvailableVehicles from '../AvailableVehicles/AvailableVehicles';

export type CheckoutParams = {
  batchNumber: string;
  uuid: string;
  type: 'new' | 'demo' | 'pre-owned';
};

export type ConfiguratorProps = {
  /**
   * Whether to show the "Configure with short code" tab
   * @default false
   */
  showConfigureTab?: boolean;
  /**
   * Custom data-testid for the component
   * @default 'configurator'
   */
  dataTestId?: string;
  /**
   * Callback when vehicle details view is shown/hidden
   */
  onDetailsViewChange?: (isShowingDetails: boolean) => void;
  /**
   * Checkout params from URL - when provided, show checkout view
   */
  checkoutParams?: CheckoutParams | null;
  /**
   * Callback when navigating to checkout view
   */
  onCheckoutNavigate?: (params: CheckoutParams) => void;
  /**
   * Callback when navigating to inventory with shortcode
   */
  onShortcodeNavigate?: (shortCode: string) => void;
  /**
   * Callback when navigating back from checkout to inventory
   */
  onCheckoutBack?: () => void;
  /**
   * Prospect details to pass to checkout for quote creation
   */
  prospectDetails?: ProspectDetails;
  /**
   * Callback when a quote is successfully created (for URL update)
   */
  onQuoteCreated?: (quoteId: string) => void;
};

/**
 * Configurator component for browsing and filtering available inventory vehicles
 * @component
 * @param props - Component props
 * @param props.showConfigureTab - Whether to show configure tab
 * @param props.dataTestId - Test ID for the component
 * @returns JSX.Element
 *
 * @example
 * <Configurator showConfigureTab onCheckoutNavigate={(params) => handleNavigate(params)} />
 */
const Configurator: React.FC<ConfiguratorProps> = ({
  showConfigureTab = false,
  dataTestId = 'configurator',
  onDetailsViewChange,
  checkoutParams,
  onCheckoutNavigate,
  onShortcodeNavigate,
  onCheckoutBack,
  prospectDetails,
  onQuoteCreated,
}) => {
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);

  // i18n labels for the configurator
  const { data: labels } = useLabels({
    id: '6dhtpdHCYn1jTZ1cRAXtJR',
    locale: convertToContentfulLocale(locale),
  });

  const [selectedVehicle, setSelectedVehicle] = useState<InventoryTableRow | null>(null);
  const [checkoutData, setCheckoutData] = useState<TransformedCheckoutDetail | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [activeTabId, setActiveTabId] = useState<string>('available-vehicles');
  const [isVinClickLoading, setIsVinClickLoading] = useState(false);

  // Track if we've already fetched checkout for the current params
  const fetchedCheckoutRef = useRef<string | null>(null);

  // Checkout detail mutation
  const { mutateAsync: getCheckoutDetail, isPending: isLoadingDetails } = useGetCheckoutDetail();

  // Fetch user location from IP (always fetch - needed for checkout API)
  const { data: location, isLoading: isLocationLoading } = useGetLocation(true);

  // Handle checkout params from URL on mount/change
  useEffect(() => {
    const fetchCheckoutFromUrl = async () => {
      if (!checkoutParams) return;

      // Wait for location to load before fetching checkout details
      if (isLocationLoading) return;

      // Create a unique key for these checkout params
      const paramsKey = `${checkoutParams.batchNumber}-${checkoutParams.uuid}`;

      // Skip if we've already fetched for these params
      if (fetchedCheckoutRef.current === paramsKey) {
        return;
      }

      // Mark as fetched immediately to prevent duplicate calls
      fetchedCheckoutRef.current = paramsKey;

      onDetailsViewChange?.(true);
      setCheckoutData(null);
      try {
        const details = await getCheckoutDetail({
          batchNumber: checkoutParams.batchNumber,
          uuid: checkoutParams.uuid,
          type: checkoutParams.type,
          zipState: prospectDetails?.stateProvince || location?.region || 'CA',
        });
        setCheckoutData(details);
        // Create a minimal vehicle object from checkout params
        setSelectedVehicle(
          createEmptyVehicleRow({
            batchNumber: checkoutParams.batchNumber,
            uuid: checkoutParams.uuid,
            condition: checkoutParams.type,
          })
        );
      } catch (err) {
        // Reset ref on error so it can be retried
        fetchedCheckoutRef.current = null;
        // Reset state to prevent infinite loader
        setSelectedVehicle(null);
        setCheckoutError('Failed to load vehicle details. Please try again.');
        onDetailsViewChange?.(false);
      }
    };
    fetchCheckoutFromUrl();
  }, [
    checkoutParams,
    getCheckoutDetail,
    location?.region,
    onDetailsViewChange,
    isLocationLoading,
    prospectDetails?.stateProvince,
  ]);

  const handleVinClick = useCallback(
    async (vehicle: InventoryTableRow) => {
      // Clear any previous error
      setCheckoutError(null);

      // Use URL-based navigation if callback provided
      if (onCheckoutNavigate) {
        // Call API first, only navigate on success
        setIsVinClickLoading(true);
        try {
          const details = await getCheckoutDetail({
            batchNumber: vehicle.batchNumber,
            uuid: vehicle.uuid,
            type: vehicle.condition,
            zipState: prospectDetails?.stateProvince || location?.region || 'CA',
          });
          // Store checkout data and mark as fetched before navigation
          // This prevents the URL-based useEffect from re-fetching
          const paramsKey = `${vehicle.batchNumber}-${vehicle.uuid}`;
          fetchedCheckoutRef.current = paramsKey;
          setCheckoutData(details);
          setSelectedVehicle(vehicle);
          onDetailsViewChange?.(true);
          // Navigate after data is ready
          onCheckoutNavigate({
            batchNumber: vehicle.batchNumber,
            uuid: vehicle.uuid,
            type: vehicle.condition,
          });
        } catch (err) {
          setCheckoutError('Failed to load vehicle details. Please try again.');
        } finally {
          setIsVinClickLoading(false);
        }
        return;
      }

      // Fallback to local state management
      setSelectedVehicle(vehicle);
      setCheckoutData(null);
      onDetailsViewChange?.(true);
      try {
        const details = await getCheckoutDetail({
          batchNumber: vehicle.batchNumber,
          uuid: vehicle.uuid,
          type: vehicle.condition,
          zipState: prospectDetails?.stateProvince || location?.region || 'CA',
        });
        setCheckoutData(details);
      } catch (err) {
        setCheckoutError('Failed to load vehicle details. Please try again.');
        setSelectedVehicle(null);
        onDetailsViewChange?.(false);
      }
    },
    [
      getCheckoutDetail,
      location?.region,
      onDetailsViewChange,
      onCheckoutNavigate,
      prospectDetails?.stateProvince,
    ]
  );

  /**
   * Handles successful shortcode checkout - sets checkout data and navigates to checkout view
   */
  const handleShortcodeCheckoutSuccess = (data: TransformedCheckoutDetail, shortCode: string) => {
    // Store checkout data
    setCheckoutData(data);

    // Create a vehicle row with available data from checkout
    setSelectedVehicle(
      createEmptyVehicleRow({
        vin: data.vin || '',
        batchNumber: data.vinBatchNumber || '',
        availability: data.availability || '',
        model: data.model || '',
        trim: data.trim || '',
        year: data.modelYear || '',
        shortCode,
        condition: (data.type as 'new' | 'demo' | 'pre-owned') || 'new',
        price: data.pricing?.total || 0,
      })
    );

    onDetailsViewChange?.(true);
    setActiveTabId('configure-short-code');

    // Navigate to inventory with shortcode in URL
    onShortcodeNavigate?.(shortCode);
  };

  const handleBackToInventory = () => {
    setSelectedVehicle(null);
    setCheckoutData(null);
    onDetailsViewChange?.(false);
    onCheckoutBack?.();
  };

  // Show checkout view when checkout params are in URL (even if API fails)
  // This ensures users see the checkout UI with an error message rather than the inventory table
  if (checkoutParams) {
    return (
      <Checkout
        vehicle={selectedVehicle}
        checkoutData={checkoutData}
        isLoading={isLoadingDetails || isLocationLoading}
        onBack={handleBackToInventory}
        dataTestId={`${dataTestId}-checkout`}
        prospectDetails={prospectDetails}
        error={checkoutError}
        onQuoteCreated={onQuoteCreated}
      />
    );
  }

  // Show vehicle details view when a vehicle is selected (local state navigation)
  if (selectedVehicle) {
    return (
      <Checkout
        vehicle={selectedVehicle}
        checkoutData={checkoutData}
        isLoading={isLoadingDetails}
        onBack={handleBackToInventory}
        dataTestId={`${dataTestId}-checkout`}
        prospectDetails={prospectDetails}
        onQuoteCreated={onQuoteCreated}
      />
    );
  }

  const availableVehiclesTab = {
    id: 'available-vehicles',
    label: labels?.['configurator.tab.availableVehicles'] ?? 'Available Vehicles',
    content: (
      <AvailableVehicles
        onVehicleSelect={handleVinClick}
        isVehicleSelectLoading={isVinClickLoading}
        error={checkoutError}
        dataTestId={`${dataTestId}-table`}
      />
    ),
  };

  const configureShortCodeTab = {
    id: 'configure-short-code',
    label: labels?.['configurator.tab.configureShortCode'] ?? 'Configure with short code',
    content: (
      <ConfigureShortCode onCheckoutSuccess={handleShortcodeCheckoutSuccess} labels={labels} />
    ),
  };

  const tabs = showConfigureTab
    ? [availableVehiclesTab, configureShortCodeTab]
    : [availableVehiclesTab];

  return (
    <Box data-testid={dataTestId}>
      <Tabs
        dataTestId={`${dataTestId}-tabs`}
        theme={theme}
        activeTab={activeTabId}
        onTabChange={setActiveTabId}
        tabs={tabs}
      />
    </Box>
  );
};

export default Configurator;
